import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, ElementRef, EventEmitter, Input, OnChanges, Output, signal, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CosmeticOptions, CustomRule, CustomRuleGroup, DisplayOptions, Filter } from '../../../models/filter-models';
import { DisplayType, EquipmentTierType } from '../../../models/enums';
import { Rarity, Comparator } from '../../../models/enums';
import { Tier } from '../../../models/item-data-models';
import { CustomRuleComponent } from '../custom-rule/custom-rule.component';
import { FilterService } from '../../../services/filter.service';
import { BaseComponent } from '../../base.component';
import { FilterTitleComponent } from '../../filter-title/filter-title.component';
import { DynamicContentComponent } from '../../dynamic-content/dynamic-content.component';
import { renumberRulesInGroup } from '../custom-rule-groups-helper';
import { confirmRemove } from '../../confirm-remove';
import type { ReorderDropHighlight } from '../custom-rule-groups-reorder.controller';

@Component({
  selector: 'app-custom-rule-group-panel',
  templateUrl: './custom-rule-group-panel.component.html',
  styleUrls: ['./custom-rule-group-panel.component.scss', '../custom-rule-groups-shared.scss'],
  imports: [FormsModule, CustomRuleComponent, NgTemplateOutlet, FilterTitleComponent, DynamicContentComponent]
})
export class CustomRuleGroupPanelComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) filter!: Filter;
  @Input({ required: true }) group!: CustomRuleGroup;
  @Input({ required: true }) cosmetic = false;
  @Input({ required: true }) showGroupHeader = false;
  @Input() headerOnly = false;
  @Input() enableMultipleGroups = true;
  @Input() enableRuleLabels = false;
  @Input({ required: true }) reorderMode = false;
  @Input() draggedRule: CustomRule | null = null;
  @Input() draggedGroup: CustomRuleGroup | null = null;
  @Input({ required: true }) totalGroupCount = 1;
  @Input() dropHighlight: ReorderDropHighlight | null = null;

  @Output() reorderModeChange = new EventEmitter<boolean>();
  @Output() ruleDragStart = new EventEmitter<{ rule: CustomRule; group: CustomRuleGroup }>();
  @Output() ruleDragEnd = new EventEmitter<void>();
  @Output() ruleDragEnterTarget = new EventEmitter<{ rule: CustomRule; group: CustomRuleGroup }>();
  @Output() ruleDragOverTarget = new EventEmitter<{ ev: DragEvent; rule: CustomRule; group: CustomRuleGroup }>();
  @Output() ruleDragEnterEnd = new EventEmitter<CustomRuleGroup>();
  @Output() groupDragEnterHeader = new EventEmitter<CustomRuleGroup>();
  @Output() groupDragOverHeader = new EventEmitter<{ ev: DragEvent; targetGroup: CustomRuleGroup }>();
  @Output() groupDragStart = new EventEmitter<CustomRuleGroup>();
  @Output() groupDragEnd = new EventEmitter<void>();
  @Output() reorderDrop = new EventEmitter<void>();
  @Output() removeGroup = new EventEmitter<CustomRuleGroup>();
  @Output() afterFilterUpdate = new EventEmitter<void>();

  Rarity = Rarity;
  Comparator = Comparator;
  Tier = Tier;

  editingName = false;

  @ViewChild('groupNameInput') groupNameInput?: ElementRef<HTMLInputElement>;

  private readonly panelInputsRevision = signal(0);

  readonly showReorderCaption = computed(() => {
    this.panelInputsRevision();
    return this.group.rules.length > 1 || (this.enableMultipleGroups && this.totalGroupCount > 1);
  });

  readonly headerDraggableForGroupReorder = computed(() => {
    this.panelInputsRevision();
    return this.enableMultipleGroups && this.reorderMode && this.totalGroupCount > 1 && !this.group.isDefault;
  });

  ngOnChanges(_changes: SimpleChanges): void {
    this.panelInputsRevision.update(v => v + 1);
  }

  toggleReorderMode() {
    this.reorderModeChange.emit(!this.reorderMode);
  }

  constructor(private filterService: FilterService) {
    super();
  }

  addRule = () => {
    if (this.cosmetic) {
      const cosmeticRule = new CustomRule();
      cosmeticRule.displayOptions.displayType = DisplayType.Custom;
      cosmeticRule.displayOptions.cosmeticOptions = new CosmeticOptions();
      cosmeticRule.continue = true;
      this.group.rules.push(cosmeticRule);
    } else {
      const newRule = new CustomRule();
      newRule.displayOptions = new DisplayOptions({ displayType: DisplayType.EquipmentTier, displayTypeTierHighlightType: EquipmentTierType.Item, displayTypeTier: Tier.EVisible });
      this.group.rules.push(newRule);
    }
    renumberRulesInGroup(this.group);
    this.panelInputsRevision.update(v => v + 1);
    this.updateFilter();
  };

  removeRule(rule: CustomRule) {
    if (!confirmRemove('Are you sure you want to remove this rule?')) return;
    const idx = this.group.rules.indexOf(rule);
    if (idx >= 0) this.group.rules.splice(idx, 1);
    renumberRulesInGroup(this.group);
    this.panelInputsRevision.update(v => v + 1);
    this.updateFilter();
  }

  cloneRule(rule: CustomRule) {
    const clone = new CustomRule();
    const original = structuredClone(rule);
    Object.assign(clone, original);
    const idx = this.group.rules.indexOf(rule);
    if (idx === -1) {
      this.group.rules.push(clone);
    } else {
      this.group.rules.splice(idx + 1, 0, clone);
    }
    renumberRulesInGroup(this.group);
    this.panelInputsRevision.update(v => v + 1);
    this.updateFilter();
  }

  updateFilter() {
    this.filterService.updateFilter();
    this.afterFilterUpdate.emit();
  }

  groupHeaderDragStart(ev: DragEvent) {
    if (this.group.isDefault) {
      ev.preventDefault();
      return;
    }
    if (!this.headerDraggableForGroupReorder()) {
      ev.preventDefault();
      return;
    }
    if (this.interactiveDragTarget(ev)) {
      ev.preventDefault();
      return;
    }
    ev.stopPropagation();
    ev.dataTransfer?.setData('text/plain', 'group');
    ev.dataTransfer!.effectAllowed = 'move';
    this.useEmptyDragImage(ev);
    this.groupDragStart.emit(this.group);
  }

  groupHeaderDragEnd() {
    this.groupDragEnd.emit();
  }

  groupHeaderDragOver(ev: DragEvent) {
    if (!this.reorderMode) return;
    ev.preventDefault();
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move';
    this.groupDragOverHeader.emit({ ev, targetGroup: this.group });
  }

  groupHeaderDragEnter(ev: DragEvent) {
    if (!this.reorderMode) return;
    ev.preventDefault();
    this.groupDragEnterHeader.emit(this.group);
  }

  private interactiveDragTarget(ev: DragEvent): boolean {
    const t = ev.target as HTMLElement | null;
    return !!t?.closest('button, a, input, textarea, select');
  }

  private static readonly emptyDragImage = (() => {
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    return img;
  })();

  private useEmptyDragImage(ev: DragEvent): void {
    ev.dataTransfer?.setDragImage(CustomRuleGroupPanelComponent.emptyDragImage, 0, 0);
  }

  ruleRowDragStart(ev: DragEvent, rule: CustomRule) {
    if (!this.reorderMode) return;
    if (this.interactiveDragTarget(ev)) {
      ev.preventDefault();
      return;
    }
    ev.stopPropagation();
    ev.dataTransfer?.setData('text/plain', 'rule');
    ev.dataTransfer!.effectAllowed = 'move';
    this.useEmptyDragImage(ev);
    this.ruleDragStart.emit({ rule, group: this.group });
  }

  ruleRowDragEnd() {
    this.ruleDragEnd.emit();
  }

  ruleRowDragOver(ev: DragEvent, rule: CustomRule) {
    if (!this.reorderMode) return;
    ev.preventDefault();
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move';
    this.ruleDragOverTarget.emit({ ev, rule, group: this.group });
  }

  ruleRowDragEnter(ev: DragEvent, rule: CustomRule) {
    if (!this.reorderMode) return;
    ev.preventDefault();
    ev.stopPropagation();
    this.ruleDragEnterTarget.emit({ rule, group: this.group });
  }

  ruleListEndDragEnter(ev: DragEvent) {
    if (!this.reorderMode) return;
    ev.preventDefault();
    ev.stopPropagation();
    this.ruleDragEnterEnd.emit(this.group);
  }

  ruleListEndDragOver(ev: DragEvent) {
    if (!this.reorderMode) return;
    ev.preventDefault();
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move';
  }

  ruleRowDrop(ev: DragEvent) {
    if (!this.reorderMode) return;
    ev.preventDefault();
    ev.stopPropagation();
    this.reorderDrop.emit();
  }

  ruleListEndDrop(ev: DragEvent) {
    if (!this.reorderMode) return;
    ev.preventDefault();
    ev.stopPropagation();
    this.reorderDrop.emit();
  }

  groupHeaderDrop(ev: DragEvent) {
    if (!this.reorderMode) return;
    ev.preventDefault();
    ev.stopPropagation();
    this.reorderDrop.emit();
  }

  toggleGroupActive() {
    this.group.active = !this.group.active;
    this.updateFilter();
  }

  startRename(ev: MouseEvent) {
    ev.preventDefault();
    this.editingName = true;
    setTimeout(() => {
      const el = this.groupNameInput?.nativeElement;
      if (!el) return;
      el.focus();
      el.select();
    });
  }

  finishRename() {
    this.editingName = false;
    this.updateFilter();
  }

  onRemoveGroupClick() {
    if (!confirmRemove('Delete this rule group and all rules inside it?')) return;
    this.removeGroup.emit(this.group);
  }
}
